/**
 * TEK-spesifikke krav per bygningsdel
 * Viser relevante paragrafer basert på TEK-versjon og bygningsdel
 *
 * OPPHAVSRETT OG KILDER:
 * - TEK17 (Forskrift om tekniske krav til byggverk): © Kommunal- og distriktsdepartementet
 *   Kilde: https://lovdata.no/dokument/SF/forskrift/2017-06-19-840
 * - TEK10: © Kommunal- og distriktsdepartementet
 *   Kilde: https://lovdata.no/dokument/SF/forskrift/2010-03-26-489
 * - NS3600:2018 (Visuell tilstandsanalyse av byggverk): © Standard Norge
 *   Kilde: https://www.standard.no
 * - Byggeforskrifter (historiske): © Kommunal- og arbeidsdepartementet
 *
 * JURIDISK INFORMASJON:
 * - Alle forskriftstekster er offentlig tilgjengelige via Lovdata.no
 * - NS3600 er opphavsrettsbeskyttet og krever lisens for full tilgang
 * - Denne koden gir kun referanser og veiledning, ikke fullstendig forskriftstekst
 */

export const TEK_KRAV_PER_BYGNINGSDEL = {
  yttertak: {
    tek17: {
      paragrafer: ['§13-3 Tetthet', '§13-4 Fuktbestandighet'],
      hovedkrav: 'Taktekking skal være tett og fuktbestandig',
      testpunkter: ['Takrenner tette', 'Takstein/papp uten skader', 'Gjennomføringer tettet']
    },
    tek10: {
      paragrafer: ['§13-3 Tetthet', '§13-4 Fuktbestandighet'],
      hovedkrav: 'Taktekking skal være tett mot regn og fukt',
      testpunkter: ['Takrenner tette', 'Takstein/papp uten skader', 'Gjennomføringer tettet']
    },
    eldre: {
      paragrafer: ['Byggeforskrift kap. 5'],
      hovedkrav: 'Tak skal være tett mot nedbør',
      testpunkter: ['Visuell kontroll tetthet', 'Takrenner funksjon']
    }
  },

  vinduer_dorer: {
    tek17: {
      paragrafer: ['§14-3 Energikrav', '§14-4 Lufttetthet'],
      hovedkrav: 'U-verdi ≤ 0.8 W/m²K (vinduer), lufttetthet klasse 3-4',
      testpunkter: ['Energimerking', 'Luftlekkasje', 'Kondens', 'Beslag funksjon']
    },
    tek10: {
      paragrafer: ['§14-3 Energikrav', '§14-4 Lufttetthet'],
      hovedkrav: 'U-verdi ≤ 1.2 W/m²K (vinduer), lufttetthet klasse 2-3',
      testpunkter: ['Energimerking', 'Luftlekkasje', 'Kondens', 'Beslag funksjon']
    },
    eldre: {
      paragrafer: ['Byggeforskrift kap. 6'],
      hovedkrav: 'Vinduer og dører skal være tette og funksjonelle',
      testpunkter: ['Visuell kontroll', 'Beslag funksjon', 'Tetthet']
    }
  },

  'bad': {
    tek17: {
      paragrafer: ['§13-11 Membran i våtrom', '§13-12 Sluk'],
      hovedkrav: 'Membran klasse A (dusjsone) og B (våtsone), fall mot sluk',
      testpunkter: ['Membran tilstede', 'Fall mot sluk', 'Sluk funksjon', 'Ventilasjon']
    },
    tek10: {
      paragrafer: ['§13-11 Membran i våtrom', '§13-12 Sluk'],
      hovedkrav: 'Membran klasse A (dusjsone) og B (våtsone), fall mot sluk',
      testpunkter: ['Membran tilstede', 'Fall mot sluk', 'Sluk funksjon', 'Ventilasjon']
    },
    eldre: {
      paragrafer: ['Byggeforskrift kap. 7'],
      hovedkrav: 'Våtrom skal være fuktsikret',
      testpunkter: ['Fuktsikring', 'Ventilasjon', 'Sluk']
    }
  },

  elektrisk: {
    tek17: {
      paragrafer: ['§16-7 Elektrisk sikkerhet'],
      hovedkrav: 'IT-system eller jordfeilbryter, tilstrekkelig antall stikkontakter',
      testpunkter: ['Sikringsskap', 'Jordfeilbryter', 'Antall stikkontakter', 'Merking']
    },
    tek10: {
      paragrafer: ['§16-7 Elektrisk sikkerhet'],
      hovedkrav: 'IT-system eller jordfeilbryter, tilstrekkelig antall stikkontakter',
      testpunkter: ['Sikringsskap', 'Jordfeilbryter', 'Antall stikkontakter']
    },
    eldre: {
      paragrafer: ['Byggeforskrift kap. 9'],
      hovedkrav: 'Elektrisk anlegg skal være forsvarlig',
      testpunkter: ['Sikringsskap', 'Generell tilstand']
    }
  },

  ventilasjon: {
    tek17: {
      paragrafer: ['§13-7 Ventilasjon'],
      hovedkrav: 'Balansert ventilasjon eller avtrekk, minimum 0.7 luftvekslinger/time',
      testpunkter: ['Type system', 'Luftmengde', 'Filter', 'Funksjon']
    },
    tek10: {
      paragrafer: ['§13-7 Ventilasjon'],
      hovedkrav: 'Balansert ventilasjon eller avtrekk, minimum 0.7 luftvekslinger/time',
      testpunkter: ['Type system', 'Luftmengde', 'Funksjon']
    },
    eldre: {
      paragrafer: ['Byggeforskrift kap. 8'],
      hovedkrav: 'Tilfredsstillende ventilasjon',
      testpunkter: ['Visuell kontroll', 'Funksjon']
    }
  }
};

/**
 * Hent relevant TEK-krav basert på bygningsdel og år
 */
export function getTekKrav(bygningsdelType, aar) {
  if (!aar) return null;

  const krav = TEK_KRAV_PER_BYGNINGSDEL[bygningsdelType];
  if (!krav) return null;

  // Bestem TEK-versjon
  if (aar >= 2017) return { ...krav.tek17, versjon: 'TEK17' };
  if (aar >= 2010) return { ...krav.tek10, versjon: 'TEK10' };
  return { ...krav.eldre, versjon: aar >= 1987 ? 'Byggeforskrift 1987' : 'Eldre forskrift' };
}

/**
 * Lag informativ melding om TEK-krav
 */
export function lagTekVeiledning(bygningsdelType, byggeaar, renoveringsaar) {
  const referanseAar = renoveringsaar || byggeaar;
  if (!referanseAar) return null;

  const krav = getTekKrav(bygningsdelType, referanseAar);
  if (!krav) return null;

  const erRenovert = !!renoveringsaar;
  const statusTekst = erRenovert ? `Renovert ${renoveringsaar}` : `Oppført ${byggeaar}`;

  return {
    referanseAar,
    versjon: krav.versjon,
    statusTekst,
    erRenovert,
    paragrafer: krav.paragrafer,
    hovedkrav: krav.hovedkrav,
    testpunkter: krav.testpunkter,
    farge: erRenovert ? '#28a745' : '#6c757d'
  };
}
